import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/common/PageContainer';
import { DataTable } from '@/components/common/DataTable';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useApi } from '@/hooks/useApi';
import { usePagination } from '@/hooks/usePagination';
import { Truck, Plus, UserPlus, Filter, ChevronRight, ShieldCheck, ShieldX, ShieldOff, Clock } from 'lucide-react';
import { Carrier, CarrierStatus, CARRIER_TYPE_LABELS, CARRIER_STATUS_LABELS, CARRIER_STATUS_COLORS, CarrierType } from '@/types/carrier.types';
import { AddCarrierModal } from '@/components/features/carriers/AddCarrierModal';
import { AddCarrierUserModal } from '@/components/features/carriers/AddCarrierUserModal';
import { CarrierFilterSidebar, CarrierFilterValues } from '@/components/features/carriers/CarrierFilterSidebar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuthContext } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';

const initialFilters: CarrierFilterValues = {
  search: '',
  status: '',
};

export default function CarriersList() {
  const navigate = useNavigate();
  const { tp } = useI18n();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const [filters, setFilters] = useState<CarrierFilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAddCarrierModal, setShowAddCarrierModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const { data, loading, get } = useApi();
  const pagination = usePagination();

  useEffect(() => {
    fetchCarriers();
  }, [pagination.page, pagination.limit, filters.search, filters.status]);

  const fetchCarriers = async () => {
    const response = await get('/api/v1/carriers', {
      page: pagination.page,
      limit: pagination.limit,
      search: filters.search,
      ...(filters.status ? { status: filters.status } : {}),
    });

    if (response?.data?.meta) {
      pagination.updatePagination({
        total_pages: response.data?.meta?.totalPages,
        total: response.data?.meta?.total,
      });
    }
  };

  const handleStatusChange = async (carrierId: string, status: CarrierStatus) => {
    setActionLoading(carrierId + status);
    try {
      await api.put(`/api/v1/carriers/${carrierId}/status`, { status });
      toast.success(tp.commonExt.updateSuccess);
      fetchCarriers();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || tp.commonExt.updateError);
    } finally {
      setActionLoading(null);
    }
  };

  const handleFilterChange = (key: keyof CarrierFilterValues, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
    pagination.reset();
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
    pagination.reset();
  };

  const carriers: Carrier[] = data?.list || [];

  const columns = [
    {
      header: tp.commonExt.name,
      accessor: (carrier: Carrier) => (
        <div className="flex items-center gap-3">
          {carrier.logo_url ? (
            <img
              src={carrier.logo_url}
              alt={carrier.name}
              className="h-8 w-8 rounded-lg object-cover"
            />
          ) : (
            <div className="p-1.5 bg-primary/10 rounded-lg">
              <Truck className="h-4 w-4 text-primary" />
            </div>
          )}
          <div>
            <div className="font-medium">{carrier.name}</div>
            {carrier.contact_info?.email && (
              <div className="text-xs text-muted-foreground">{carrier.contact_info.email}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      header: tp.commonExt.status,
      accessor: (carrier: Carrier) => (
        <Badge
          variant="outline"
          className={CARRIER_STATUS_COLORS[carrier.status] ?? 'bg-muted text-muted-foreground'}
        >
          {carrier.status === 'pending_review' && <Clock className="h-3 w-3 ml-1" />}
          {CARRIER_STATUS_LABELS[carrier.status] ?? carrier.status}
        </Badge>
      ),
    },
    {
      header: tp.commonExt.type,
      accessor: (carrier: Carrier) => (
        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
          {CARRIER_TYPE_LABELS[carrier.type as CarrierType]}
        </Badge>
      ),
    },
    {
      header: tp.commonExt.phone,
      accessor: (carrier: Carrier) => carrier.contact_info?.phone || '-',
      className: 'text-muted-foreground',
    },
  ];

  const renderActions = (carrier: Carrier) => (
    <>
      {isAdmin && carrier.status === 'pending_review' && (
        <Button variant="ghost" size="icon" className="h-8 w-8 text-green-600 hover:text-green-700" disabled={actionLoading === carrier.id + 'approved'} onClick={(e) => { e.stopPropagation(); handleStatusChange(carrier.id, 'approved'); }} title={tp.carriers.approve}>
          <ShieldCheck className="h-4 w-4" />
        </Button>
      )}
      {isAdmin && (carrier.status === 'pending_review' || carrier.status === 'approved' || carrier.status === 'onboarding') && (
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" disabled={actionLoading === carrier.id + 'suspended'} onClick={(e) => { e.stopPropagation(); handleStatusChange(carrier.id, 'suspended'); }} title={tp.carriers.suspend}>
          <ShieldX className="h-4 w-4" />
        </Button>
      )}
      {isAdmin && carrier.status === 'suspended' && (
        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={actionLoading === carrier.id + 'approved'} onClick={(e) => { e.stopPropagation(); handleStatusChange(carrier.id, 'approved'); }} title={tp.carriers.unsuspend}>
          <ShieldOff className="h-4 w-4" />
        </Button>
      )}
    </>
  );

  const FilterSidebarContent = (
    <CarrierFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">{tp.carriers.listTitle}</h1>
              <p className="text-sm text-muted-foreground">{tp.carriers.listSubtitle}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">{FilterSidebarContent}</div>
              </SheetContent>
            </Sheet>
            <Button variant="outline" onClick={() => setShowAddUserModal(true)}>
              <UserPlus className="h-4 w-4 ml-2" />
              {tp.carriers.addUser}
            </Button>
            <Button onClick={() => setShowAddCarrierModal(true)}>
              <Plus className="h-4 w-4 ml-2" />
              {tp.carriers.addCarrier}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex gap-4 h-[calc(100vh-180px)]">
        <div className="hidden lg:block h-full overflow-y-auto">
          {FilterSidebarContent}
        </div>
        <div className="flex-1 min-w-0">
          {loading && !data ? (
            <LoadingSkeleton type="table" rows={8} />
          ) : carriers.length === 0 ? (
            <EmptyState
              icon={<Truck className="h-16 w-16" />}
              title={tp.carriers.noCarriers}
              description={tp.carriers.noCarriersDescription}
              action={{ label: tp.carriers.addCarrier, onClick: () => setShowAddCarrierModal(true) }}
            />
          ) : (
            <DataTable
              data={carriers}
              columns={columns}
              actions={renderActions}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              pagination={{
                currentPage: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                onPageChange: pagination.goToPage,
              }}
            />
          )}
        </div>
      </div>

      <AddCarrierModal
        open={showAddCarrierModal}
        onClose={() => setShowAddCarrierModal(false)}
        onSuccess={fetchCarriers}
      />

      <AddCarrierUserModal
        open={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onSuccess={fetchCarriers}
        carriers={carriers}
      />
    </PageContainer>
  );
}
